(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-hl-road-intro]';
  const PLAYED_KEY = 'hl-road-intro:v1:seen';
  const EASE = 'cubic-bezier(0.76, 0, 0.24, 1)';
  const controlledRoots = new WeakMap();

  const sleep = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const numberFromData = (element, key, fallback) => {
    const value = Number.parseInt(element.dataset[key], 10);
    return Number.isFinite(value) ? value : fallback;
  };

  const clamp = (value, minimum, maximum) =>
    Math.min(Math.max(value, minimum), maximum);

  const safeQueryAll = (selector) => {
    if (!selector) return [];

    try {
      return Array.from(document.querySelectorAll(selector));
    } catch (error) {
      console.warn('[Hasbro road intro] Ignoring an invalid selector:', selector, error);
      return [];
    }
  };

  const isVisible = (element) => {
    if (!(element instanceof Element)) return false;

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return (
      rect.width > 1 &&
      rect.height > 1 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  };

  const releaseBootLock = () => {
    window.clearTimeout(window.__HL_ROAD_INTRO_FAILSAFE__);
    delete window.__HL_ROAD_INTRO_FAILSAFE__;
    const earlyEscape = window.__HL_ROAD_INTRO_EARLY_ESCAPE__;
    if (earlyEscape) {
      document.removeEventListener('keydown', earlyEscape, { capture: true });
      delete window.__HL_ROAD_INTRO_EARLY_ESCAPE__;
    }
    document.documentElement.classList.remove('hl-intro-pending', 'hl-intro-running');
  };

  const wasPlayed = () => {
    try {
      return window.sessionStorage.getItem(PLAYED_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  };

  const rememberPlayed = () => {
    try {
      window.sessionStorage.setItem(PLAYED_KEY, 'true');
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes. Playback still works.
    }
  };

  const waitForEssentialVisuals = async (root, destinationLogo) => {
    const proxyLogo = root.querySelector('img');
    const destinationImage = destinationLogo?.matches?.('img')
      ? destinationLogo
      : destinationLogo?.querySelector?.('img');
    const images = [proxyLogo, destinationImage].filter(
      (image, index, list) => image && list.indexOf(image) === index
    );
    const tasks = [];

    images.forEach((image) => {
      if (typeof image.decode === 'function') {
        tasks.push(image.decode().catch(() => undefined));
      } else if (!image.complete) {
        tasks.push(
          new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          })
        );
      }
    });

    const asphaltValue = window
      .getComputedStyle(root)
      .getPropertyValue('--hl-asphalt-image')
      .trim();
    const asphaltMatch = asphaltValue.match(/^url\(["']?(.*?)["']?\)$/i);
    if (asphaltMatch?.[1]) {
      tasks.push(
        new Promise((resolve) => {
          const asphalt = new Image();
          asphalt.onload = resolve;
          asphalt.onerror = resolve;
          asphalt.src = asphaltMatch[1];
        })
      );
    }

    if (document.fonts?.ready) {
      tasks.push(document.fonts.ready.catch(() => undefined));
    }

    await Promise.race([
      Promise.allSettled(tasks),
      sleep(1500)
    ]);
  };

  const waitForIntroStyles = async (root, topPanel, logoMover) => {
    const deadline = window.performance.now() + 1800;

    while (window.performance.now() < deadline) {
      const rootStyle = window.getComputedStyle(root);
      const moverStyle = window.getComputedStyle(logoMover);
      const panelHeight = topPanel.getBoundingClientRect().height;

      if (
        rootStyle.position === 'fixed' &&
        rootStyle.display !== 'none' &&
        moverStyle.position === 'fixed' &&
        panelHeight > 20
      ) {
        return true;
      }

      await sleep(50);
    }

    return false;
  };

  const chooseHeader = (selector) => {
    const candidates = safeQueryAll(selector)
      .filter(isVisible)
      .map((element) => ({ element, rect: element.getBoundingClientRect() }))
      .filter(({ rect }) => rect.top < 40 && rect.bottom > 40)
      .sort((a, b) => b.rect.bottom - a.rect.bottom);

    return candidates[0]?.element || null;
  };

  const chooseLogoTarget = (selector, root) =>
    safeQueryAll(selector).find((element) => !root.contains(element) && isVisible(element)) || null;

  const chooseRevealNodes = (selector, root, logoTarget) => {
    const nodes = safeQueryAll(selector).filter(
      (element) =>
        !root.contains(element) &&
        element !== logoTarget &&
        (!logoTarget || !element.contains(logoTarget)) &&
        isVisible(element)
    );

    /* Prefer the deepest matches so individual nav items/icons can stagger. */
    return nodes.filter(
      (node) => !nodes.some((other) => other !== node && node.contains(other))
    );
  };

  const fallbackLogoRect = () => {
    const narrow = window.innerWidth < 750;
    const width = narrow
      ? clamp(window.innerWidth * 0.42, 132, 182)
      : clamp(window.innerWidth * 0.112, 185, 230);

    return {
      left: narrow ? 16 : Math.max(24, window.innerWidth * 0.029),
      top: narrow ? 12 : 16,
      width,
      height: width * 0.3
    };
  };

  const prepareLogoMover = (mover) => {
    const rect = mover.getBoundingClientRect();

    /* Freeze the CSS-centered box into pixel geometry before FLIP transforms. */
    Object.assign(mover.style, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      maxHeight: 'none',
      transform: 'translate3d(0, 0, 0)'
    });

    return rect;
  };

  const createAnimation = (element, keyframes, options, animationList) => {
    const animation = element.animate(keyframes, {
      fill: 'both',
      easing: EASE,
      ...options
    });

    animationList.push(animation);
    return animation;
  };

  const immediateExit = (root, markPlayed = false) => {
    if (markPlayed) rememberPlayed();
    releaseBootLock();
    root.remove();
    document.dispatchEvent(new CustomEvent('hasbro:intro:skipped'));
  };

  const initialize = async (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const earlyEscape = window.__HL_ROAD_INTRO_EARLY_ESCAPE__;
    if (earlyEscape) {
      document.removeEventListener('keydown', earlyEscape, { capture: true });
      delete window.__HL_ROAD_INTRO_EARLY_ESCAPE__;
    }
    /* The main controller owns failure cleanup from this point forward. */
    window.clearTimeout(window.__HL_ROAD_INTRO_FAILSAFE__);
    delete window.__HL_ROAD_INTRO_FAILSAFE__;
    const earlyAborted = window.__HL_ROAD_INTRO_EARLY_ABORTED__ === true;
    delete window.__HL_ROAD_INTRO_EARLY_ABORTED__;

    const playback = root.dataset.playback || 'once_session';
    const parameters = new URLSearchParams(window.location.search);
    const queryOverride = parameters.get('hl_intro');
    const themeRole = String(
      root.dataset.themeRole ||
      window.Shopify?.theme?.role ||
      ''
    ).toLowerCase();
    const isThemePreview =
      parameters.has('preview_theme_id') ||
      (themeRole.length > 0 && themeRole !== 'main');
    const forcePlayback = queryOverride === '1';
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const navigationType = window.performance
      .getEntriesByType?.('navigation')?.[0]?.type;
    const shouldSkip =
      earlyAborted ||
      queryOverride === '0' ||
      motionPreference.matches ||
      window.Shopify?.designMode ||
      (!forcePlayback &&
        (playback === 'disabled' ||
          window.location.hash.length > 0 ||
          navigationType === 'back_forward' ||
          (playback === 'once_session' && wasPlayed())));

    if (shouldSkip) {
      immediateExit(
        root,
        playback === 'once_session' && motionPreference.matches && !isThemePreview
      );
      return;
    }

    const state = {
      animations: [],
      revealNodes: [],
      logoTarget: null,
      header: null,
      inertRecords: [],
      hardStop: 0,
      finalized: false,
      handoffDone: false
    };
    const previousFocus =
      document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null;
    const blockedKeys = new Set([
      'Tab',
      'Enter',
      'PageDown',
      'PageUp',
      'Home',
      'End',
      'ArrowDown',
      'ArrowUp',
      ' '
    ]);
    let finalize = () => undefined;
    const stopKeyboardInteraction = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finalize({ completed: false });
        return;
      }
      if (blockedKeys.has(event.key)) event.preventDefault();
    };
    const finishOnPageHide = () => finalize({ completed: false });
    const finishOnOrientationChange = () => finalize({ completed: false });
    const finishOnReducedMotion = (event) => {
      if (event.matches) finalize({ completed: false });
    };

    finalize = ({ completed = true } = {}) => {
      if (state.finalized) return;
      state.finalized = true;

      window.clearTimeout(state.hardStop);
      state.animations.forEach((animation) => {
        try {
          animation.cancel();
        } catch (_error) {
          // Continue cleanup even if a browser rejects cancellation.
        }
      });
      state.revealNodes.forEach((element) =>
        element.classList.remove('hl-intro-controlled')
      );
      state.logoTarget?.classList.remove('hl-intro-logo-target');
      state.header?.classList.remove(
        'hl-intro-header-handoff',
        'hl-intro-header-positioned'
      );
      document.removeEventListener('keydown', stopKeyboardInteraction, { capture: true });
      window.removeEventListener('pagehide', finishOnPageHide);
      window.removeEventListener('orientationchange', finishOnOrientationChange);
      motionPreference.removeEventListener?.('change', finishOnReducedMotion);

      state.inertRecords.forEach(({ element, hadInert }) => {
        if (element.isConnected && !hadInert) element.removeAttribute('inert');
      });

      releaseBootLock();
      root.dataset.status = completed ? 'complete' : 'cancelled';
      root.remove();

      if (previousFocus?.isConnected) {
        try {
          previousFocus.focus({ preventScroll: true });
        } catch (_error) {
          // Focus restoration is best-effort only.
        }
      }

      document.dispatchEvent(
        new CustomEvent('hasbro:intro:complete', {
          detail: { completed, playback }
        })
      );
    };

    /* Register cleanup before the first global mutation or asynchronous wait. */
    controlledRoots.set(root, finalize);

    document.documentElement.classList.add('hl-intro-pending');
    const viewportHeight =
      root.getBoundingClientRect().height ||
      window.visualViewport?.height ||
      window.innerHeight;
    root.style.setProperty('--hl-viewport-height', `${viewportHeight}px`);
    if (playback === 'once_session') rememberPlayed();

    /* Make every branch outside the temporary intro inert, preserving prior state. */
    const recordedInertElements = new Set();
    let activeBranch = root;
    while (activeBranch?.parentElement) {
      const parent = activeBranch.parentElement;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === activeBranch || recordedInertElements.has(sibling)) return;
        const hadInert = sibling.hasAttribute('inert');
        state.inertRecords.push({ element: sibling, hadInert });
        recordedInertElements.add(sibling);
        sibling.setAttribute('inert', '');
      });
      if (parent === document.body) break;
      activeBranch = parent;
    }

    document.addEventListener('keydown', stopKeyboardInteraction, { capture: true });
    window.addEventListener('pagehide', finishOnPageHide, { once: true });
    window.addEventListener('orientationchange', finishOnOrientationChange, { once: true });
    motionPreference.addEventListener?.('change', finishOnReducedMotion);

    const preflightLogoTarget = safeQueryAll(root.dataset.logoSelector).find(
      (element) => !root.contains(element)
    );
    const readinessStartedAt = window.performance.now();
    await waitForEssentialVisuals(root, preflightLogoTarget);
    if (state.finalized || !root.isConnected) return;

    /* Do not cover a restored page that is already meaningfully scrolled. */
    if (window.scrollY > 48) {
      finalize({ completed: false });
      return;
    }

    const topPanel = root.querySelector('[data-hl-panel="top"]');
    const bottomPanel = root.querySelector('[data-hl-panel="bottom"]');
    const logoMover = root.querySelector('[data-hl-logo-mover]');
    const roadLines = Array.from(root.querySelectorAll('.hl-road-intro__road-line'));

    if (
      !topPanel ||
      !bottomPanel ||
      !logoMover ||
      typeof topPanel.animate !== 'function'
    ) {
      console.warn('[Hasbro road intro] Required animation support or markup was not found.');
      finalize({ completed: false });
      return;
    }

    const stylesReady = await waitForIntroStyles(root, topPanel, logoMover);
    if (state.finalized || !root.isConnected) return;
    if (!stylesReady) {
      console.warn('[Hasbro road intro] Intro styles were not ready before the safety timeout.');
      finalize({ completed: false });
      return;
    }

    state.header = chooseHeader(root.dataset.headerSelector);
    state.logoTarget = chooseLogoTarget(root.dataset.logoSelector, root);
    state.revealNodes = chooseRevealNodes(
      root.dataset.revealSelector,
      root,
      state.logoTarget
    );

    const configuredHold = clamp(numberFromData(root, 'hold', 750), 300, 1600);
    const readinessElapsed = Math.round(window.performance.now() - readinessStartedAt);
    const hold = Math.max(0, configuredHold - readinessElapsed);
    const desktopTravel = clamp(numberFromData(root, 'travel', 2000), 1000, 3600);
    const travel = window.innerWidth < 750 ? Math.round(desktopTravel * 0.76) : desktopTravel;

    state.revealNodes.forEach((element) => element.classList.add('hl-intro-controlled'));

    const startLogoRect = prepareLogoMover(logoMover);
    const targetRect = state.logoTarget?.getBoundingClientRect();
    const sourceAspect = startLogoRect.width / Math.max(startLogoRect.height, 1);
    const targetAspect = targetRect
      ? targetRect.width / Math.max(targetRect.height, 1)
      : 0;
    const unboundedScale = targetRect
      ? targetRect.width / Math.max(startLogoRect.width, 1)
      : 0;
    const validLogoTarget = Boolean(
      state.logoTarget &&
        state.header?.contains(state.logoTarget) &&
        startLogoRect.width > 10 &&
        startLogoRect.height > 10 &&
        targetRect.width > 10 &&
        targetRect.height > 10 &&
        targetRect.right > 0 &&
        targetRect.left < window.innerWidth &&
        unboundedScale >= 0.08 &&
        unboundedScale <= 0.85 &&
        Math.abs(targetAspect / sourceAspect - 1) <= 0.35
    );
    const destinationRect = validLogoTarget ? targetRect : fallbackLogoRect();
    const logoScale = clamp(destinationRect.width / startLogoRect.width, 0.08, 1);
    const logoTranslateX = destinationRect.left - startLogoRect.left;
    const logoTranslateY = destinationRect.top - startLogoRect.top;
    if (validLogoTarget) state.logoTarget.classList.add('hl-intro-logo-target');

    const topRect = topPanel.getBoundingClientRect();
    const bottomRect = bottomPanel.getBoundingClientRect();
    const measuredHeaderBottom = state.header?.getBoundingClientRect().bottom || 0;
    const headerHeight = clamp(
      measuredHeaderBottom > 0 ? measuredHeaderBottom : window.innerWidth < 750 ? 76 : 154,
      window.innerWidth < 750 ? 56 : 72,
      viewportHeight * 0.3
    );
    const topDistance = Math.max(0, topRect.bottom - headerHeight);
    const bottomLip = window.innerWidth < 750 ? 7 : 10;
    const bottomDistance = Math.max(0, bottomRect.height - bottomLip);

    if (startLogoRect.width <= 10 || startLogoRect.height <= 10 || topDistance < 8) {
      console.warn('[Hasbro road intro] Measured geometry was not safe to animate. ' + JSON.stringify({
        startLogoWidth: startLogoRect.width,
        startLogoHeight: startLogoRect.height,
        topDistance,
        topPanelBottom: topRect.bottom,
        headerHeight,
        viewportHeight,
        viewportWidth: window.innerWidth
      }));
      finalize({ completed: false });
      return;
    }

    document.documentElement.classList.remove('hl-intro-pending');
    document.documentElement.classList.add('hl-intro-running');
    root.dataset.status = 'running';

    createAnimation(
      topPanel,
      [
        { transform: 'translate3d(0, 0, 0)', offset: 0 },
        { transform: `translate3d(0, ${-topDistance * 0.86}px, 0)`, offset: 0.78 },
        { transform: `translate3d(0, ${-topDistance}px, 0)`, offset: 1 }
      ],
      { delay: hold, duration: travel },
      state.animations
    );

    createAnimation(
      bottomPanel,
      [
        { transform: 'translate3d(0, 0, 0)', offset: 0 },
        { transform: `translate3d(0, ${bottomDistance * 0.86}px, 0)`, offset: 0.78 },
        { transform: `translate3d(0, ${bottomDistance}px, 0)`, offset: 1 }
      ],
      { delay: hold, duration: travel },
      state.animations
    );

    roadLines.forEach((roadLine) => {
      createAnimation(
        roadLine,
        [
          { transform: 'translateZ(0) scaleY(1.84)' },
          { transform: 'translateZ(0) scaleY(1)' }
        ],
        { delay: hold, duration: Math.round(travel * 0.48) },
        state.animations
      );
    });

    const logoAnimation = createAnimation(
      logoMover,
      [
        { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1, offset: 0 },
        {
          transform: `translate3d(${logoTranslateX * 0.9}px, ${logoTranslateY * 0.9}px, 0) scale(${logoScale + (1 - logoScale) * 0.08})`,
          opacity: 1,
          offset: 0.82
        },
        {
          transform: `translate3d(${logoTranslateX}px, ${logoTranslateY}px, 0) scale(${logoScale})`,
          opacity: 1,
          offset: 1
        }
      ],
      {
        delay: hold + Math.round(travel * 0.04),
        duration: Math.round(travel * 0.7)
      },
      state.animations
    );

    const performHeaderHandoff = () => {
      if (state.finalized || state.handoffDone || !state.header || !validLogoTarget) return;
      state.handoffDone = true;
      /* Visibility is outside the animated opacity cascade and prevents a
         filled WAAPI effect from leaving a ghost copy behind. */
      logoMover.style.visibility = 'hidden';
      if (window.getComputedStyle(state.header).position === 'static') {
        state.header.classList.add('hl-intro-header-positioned');
      }
      state.header.classList.add('hl-intro-header-handoff');
      if (validLogoTarget) state.logoTarget.classList.remove('hl-intro-logo-target');
    };
    logoAnimation.finished.then(performHeaderHandoff).catch(() => undefined);

    state.revealNodes.forEach((element, index) => {
      createAnimation(
        element,
        [
          { opacity: 0, transform: 'translate3d(0, -9px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ],
        {
          delay: hold + Math.round(travel * 0.74) + index * 65,
          duration: 520
        },
        state.animations
      );
    });

    state.hardStop = window.setTimeout(
      () => finalize({ completed: false }),
      hold + travel + 1800
    );

    await Promise.allSettled(state.animations.map((animation) => animation.finished));
    finalize({ completed: true });
  };

  const initializeAll = () => {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));

    if (roots.length === 0) {
      releaseBootLock();
      return;
    }

    roots.forEach((root) => {
      initialize(root).catch((error) => {
        console.error('[Hasbro road intro] Animation failed safely:', error);
        const finalize = controlledRoots.get(root);
        if (finalize) {
          finalize({ completed: false });
        } else {
          immediateExit(root, false);
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    if (root && !window.Shopify?.designMode) initialize(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    controlledRoots.get(root)?.({ completed: false });
  });
})();
